import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, X } from "lucide-react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import OrgChartCanvas, { DEPT_COLORS } from "@/components/org/OrgChartCanvas";
import DiagnosticsPanel from "@/components/org/DiagnosticsPanel";
import SuccessionPanel from "@/components/org/SuccessionPanel";
import ScenarioSandbox from "@/components/org/ScenarioSandbox";
import AdvisorPanel from "@/components/org/AdvisorPanel";

const BAND_STYLE = { low: "bg-emerald-50 text-emerald-700", medium: "bg-amber-50 text-amber-800", high: "bg-red-50 text-red-700" };

function SelectedPanel({ emp, overlays, compVisible, findings }) {
  const [attrition, setAttrition] = useState(null);
  useEffect(() => {
    setAttrition(null);
    if (!emp) return;
    api.get(`/org/attrition/${emp.id}`).then((r) => setAttrition(r.data)).catch(() => setAttrition(false));
  }, [emp]);

  if (!emp) {
    return (
      <div className="text-[12.5px] text-muted-foreground p-5">
        Select a node to inspect skill density, compensation band, attrition drivers and structural flags.
      </div>
    );
  }
  const od = overlays?.[emp.id] || {};
  const affecting = (findings || []).filter((f) => f.affected_employee_ids.includes(emp.id));

  return (
    <div className="p-5 space-y-4" data-testid="org-selected-panel">
      <div>
        <p className="text-[15px] font-bold text-[var(--brand-primary)]">{emp.name}</p>
        <p className="text-[12px] text-slate-500">{emp.title}</p>
        <p className="text-[10px] font-mono text-muted-foreground mt-1">
          <span className="inline-block h-2 w-2 mr-1.5" style={{ background: DEPT_COLORS[emp.department] }} />
          {emp.department} · L{emp.level} · {emp.id}
        </p>
      </div>

      {od.skill_density != null && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Skill density</p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-1.5 bg-slate-100">
              <div className={`h-1.5 ${od.skill_density >= 70 ? "bg-teal-600" : od.skill_density >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                style={{ width: `${od.skill_density}%` }} />
            </div>
            <span className="text-[11px] font-mono text-slate-600">{od.skill_density}%</span>
          </div>
          {od.role_profile && (
            <p className="text-[11px] text-slate-500 mt-1.5">
              {od.role_readiness}% ready vs <span className="font-medium">{od.role_profile}</span> profile
            </p>
          )}
        </div>
      )}

      {compVisible && od.compensation && (
        <div data-testid="selected-comp">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Compensation</p>
          <p className="text-[14px] font-bold text-[var(--brand-primary)] mt-0.5">
            QAR {od.compensation.total.toLocaleString()}<span className="text-[11px] font-mono text-slate-500 ml-2">band {od.compensation.band}</span>
          </p>
        </div>
      )}

      {attrition && attrition !== false && (
        <div data-testid="selected-attrition">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Attrition risk</p>
          <p className="mt-1">
            <span className={`text-[11px] font-mono px-2 py-0.5 ${BAND_STYLE[attrition.band]}`}>
              {attrition.score}/100 · {attrition.band.toUpperCase()}
            </span>
          </p>
          <div className="mt-2 space-y-1.5">
            {attrition.factors.map((f) => (
              <div key={f.factor} className="flex items-start justify-between gap-2">
                <span className="text-[11px] text-slate-600 leading-snug">{f.note}</span>
                <span className="text-[11px] font-mono text-slate-500 shrink-0">+{f.points}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {affecting.length > 0 && (
        <div data-testid="selected-findings">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Structural flags</p>
          {affecting.map((f) => (
            <p key={f.id} className="text-[11px] text-red-700 mt-1.5 leading-snug border-l-2 border-red-400 pl-2">{f.title}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrgIntelligencePage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState(null);
  const [overlaysData, setOverlaysData] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [overlay, setOverlay] = useState("reporting");
  const [selected, setSelected] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [tab, setTab] = useState("chart");

  const isEmployee = user.role === "employee";
  const canSandbox = ["admin", "dept_head", "hr_manager"].includes(user.role);
  const canAdvise = ["admin", "hr_manager"].includes(user.role);

  useEffect(() => {
    api.get("/org/chart").then((r) => setEmployees(r.data.employees)).catch(() => {});
    api.get("/org/overlays").then((r) => setOverlaysData(r.data)).catch(() => {});
    if (!isEmployee) api.get("/org/diagnostics").then((r) => setDiagnostics(r.data)).catch(() => {});
  }, [isEmployee]);

  const compVisible = overlaysData?.compensation_visible;
  const viewOnChart = (ids) => { setHighlight(new Set(ids)); setTab("chart"); };
  const [packing, setPacking] = useState(false);
  const downloadBoardPack = async () => {
    setPacking(true);
    try {
      await downloadFile("/org/board-pack", "aetria-board-pack.pdf");
      toast.success("Board Pack downloaded");
    } catch {
      toast.error("Board Pack export failed");
    } finally {
      setPacking(false);
    }
  };

  if (!employees) return <Skeleton className="h-96 w-full rounded-none" />;

  return (
    <div className="space-y-5 fade-in" data-testid="org-intelligence-page">
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-stretch gap-3">
        <TabsList className="rounded-none bg-white border border-border h-auto p-0 justify-start flex-1 overflow-x-auto">
          {[
            ["chart", "Org Chart", true],
            ["diagnostics", "Diagnostics", !isEmployee],
            ["succession", "Succession Risk", !isEmployee],
            ["sandbox", "Scenario Sandbox", canSandbox],
            ["advisor", "Trajectory Advisor", canAdvise],
          ].filter(([, , show]) => show).map(([value, label]) => (
            <TabsTrigger key={value} value={value} data-testid={`org-tab-${value}`}
              className="rounded-none px-5 py-2.5 text-[13px] data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-white data-[state=active]:shadow-none border-r border-border last:border-r-0">
              {label}
              {value === "diagnostics" && diagnostics?.findings?.length > 0 && (
                <span className="ml-2 text-[9px] font-mono bg-red-500 text-white px-1.5 py-px">{diagnostics.findings.length}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        {canAdvise && (
          <Button onClick={downloadBoardPack} disabled={packing} data-testid="board-pack-btn"
            className="rounded-none h-auto text-[13px] bg-teal-700 hover:bg-teal-800 text-white shrink-0">
            <FileDown className="h-4 w-4" /> Board Pack (PDF)
          </Button>
        )}
        </div>

        <TabsContent value="chart" className="mt-5 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">Overlay</span>
            <ToggleGroup type="single" value={overlay} onValueChange={(v) => v && setOverlay(v)}
              className="border border-border bg-white gap-0 divide-x divide-border rounded-none">
              {[["reporting", "Reporting"], ["density", "Skill Density"],
                ...(compVisible ? [["comp", "Compensation"]] : []), ["attrition", "Attrition Risk"]].map(([v, l]) => (
                <ToggleGroupItem key={v} value={v} data-testid={`overlay-toggle-${v}`}
                  className="rounded-none h-8 px-3.5 text-[12px] data-[state=on]:bg-teal-700 data-[state=on]:text-white">
                  {l}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            {highlight && (
              <button onClick={() => setHighlight(null)} data-testid="clear-highlight-btn"
                className="inline-flex items-center gap-1 text-[11px] text-red-700 border border-red-300 px-2.5 py-1.5 hover:bg-red-50">
                <X className="h-3 w-3" /> Clear finding highlight ({highlight.size} nodes)
              </button>
            )}
            <span className="text-[11px] font-mono text-muted-foreground ml-auto">
              {employees.length} employees · scroll to zoom · drag to pan
            </span>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4 items-start">
            <OrgChartCanvas
              employees={employees}
              overlay={overlay}
              overlayData={overlaysData?.overlays || {}}
              selectedId={selected?.id}
              onSelect={setSelected}
              highlightIds={highlight}
              height={620}
            />
            <div className="bg-white border border-border min-h-[200px] xl:sticky xl:top-20">
              <SelectedPanel emp={selected} overlays={overlaysData?.overlays} compVisible={compVisible}
                findings={diagnostics?.findings} />
            </div>
          </div>
        </TabsContent>

        {!isEmployee && (
          <TabsContent value="diagnostics" className="mt-5">
            {diagnostics ? <DiagnosticsPanel data={diagnostics} onViewChart={viewOnChart} />
              : <Skeleton className="h-64 w-full rounded-none" />}
          </TabsContent>
        )}

        {!isEmployee && (
          <TabsContent value="succession" className="mt-5">
            <SuccessionPanel />
          </TabsContent>
        )}

        {canSandbox && (
          <TabsContent value="sandbox" className="mt-5">
            <ScenarioSandbox canEdit={["admin", "dept_head"].includes(user.role)} />
          </TabsContent>
        )}

        {canAdvise && (
          <TabsContent value="advisor" className="mt-5">
            <AdvisorPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
